import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, NotebookPen, Paperclip, Plus, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field, FormSection } from '../../components/FormBits'
import { toastError } from '../../ui'
import { useSaveNote } from '../api'
import type { PersonalNote } from '../types'

/** Not içeriği düz metindir (satır sonları korunur); HTML işlenmez. */
export function NoteForm({ note, onDone }: { note: PersonalNote | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveNote()
  const fileRef = useRef<HTMLInputElement>(null)
  const [tagDraft, setTagDraft] = useState('')
  const schema = z.object({
    title: z.string().trim().min(2, t('finance.personal.form.titleRequired')).max(80),
    tags: z.array(z.string().max(24)).max(8),
    body: z.string().trim().max(5000),
    pinned: z.boolean(),
    remindAt: z.string(),
    attachmentName: z.string(),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { title: note?.title ?? '', tags: note?.tags ?? [], body: note?.body ?? '', pinned: note?.pinned ?? false, remindAt: note?.remindAt ?? '', attachmentName: note?.attachmentName ?? '' },
  })
  const { errors } = form.formState
  const [tags, attachmentName] = useWatch({ control: form.control, name: ['tags', 'attachmentName'] })

  const addTag = () => {
    const v = tagDraft.trim()
    if (!v || tags.includes(v) || tags.length >= 8) return
    form.setValue('tags', [...tags, v])
    setTagDraft('')
  }

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ id: note?.id, input: { ...v, remindAt: v.remindAt || null } })
      toast.success(t(note ? 'finance.personal.noteUpdated' : 'finance.personal.noteCreated'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<NotebookPen />}
      title={t(note ? 'finance.personal.form.editNote' : 'finance.personal.form.createNote')}
      description={t('finance.personal.privateHint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('finance.personal.form.saveNote')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="pn-title" label={t('finance.personal.form.title')} error={errors.title?.message} full>
          <Input id="pn-title" placeholder={t('finance.personal.form.titlePlaceholder')} aria-invalid={!!errors.title} {...form.register('title')} />
        </Field>
        <Field id="pn-tag" label={t('finance.personal.form.tags')} full>
          <div className="flex gap-2">
            <Input
              id="pn-tag"
              value={tagDraft}
              maxLength={24}
              placeholder={t('finance.personal.form.tagPlaceholder')}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addTag()
                }
              }}
            />
            <Button type="button" variant="outline" size="icon" onClick={addTag} aria-label={t('finance.personal.form.addTag')}>
              <Plus />
            </Button>
          </div>
          {tags.length ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {tags.map((tag) => (
                <Badge key={tag} tone="primary" className="gap-1 pr-1">
                  {tag}
                  <button type="button" className="rounded-full p-0.5 hover:bg-primary/20" aria-label={t('finance.personal.form.removeTag', { tag })} onClick={() => form.setValue('tags', tags.filter((x) => x !== tag))}>
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}
        </Field>
        <Field id="pn-body" label={t('finance.personal.form.body')} full>
          <Textarea id="pn-body" rows={6} maxLength={5000} placeholder={t('finance.personal.form.bodyPlaceholder')} {...form.register('body')} />
        </Field>
        <Field id="pn-remind" label={t('finance.personal.form.remindAt')}>
          <Controller control={form.control} name="remindAt" render={({ field }) => <DateField id="pn-remind" value={field.value} onChange={field.onChange} />} />
        </Field>
        <div className="flex flex-col justify-end">
          <CheckRow {...form.register('pinned')}>{t('finance.personal.form.pinned')}</CheckRow>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 sm:col-span-2">
          <Paperclip className="size-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{t('finance.personal.form.attachment')}</p>
            <p className="truncate text-xs text-muted-foreground">{attachmentName || t('finance.personal.form.attachmentHint')}</p>
          </div>
          <input ref={fileRef} type="file" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => form.setValue('attachmentName', e.target.files?.[0]?.name ?? '')} />
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
            {t('finance.pay.form.pickFile')}
          </Button>
        </div>
      </FormSection>
    </Modal>
  )
}
